import { useEffect, useState } from "react";
import useSWR from "swr";
import { Pencil, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, brl, errMsg, fetcher, formatDate } from "../../lib/api";
import { PageHeader, Field, GoldButton, GhostButton } from "../../components/admin/AdminUI";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import ConfirmDelete from "../../components/admin/ConfirmDelete";

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const status = (p) => {
  const n = new Date();
  if (!p.active) return ["Desativada", "bg-white/10 text-[#A1A1AA]"];
  if (new Date(p.start_at) > n) return ["Agendada", "bg-sky-500/15 text-sky-300"];
  if (new Date(p.end_at) < n) return ["Encerrada", "bg-white/10 text-[#A1A1AA]"];
  return ["Ativa", "bg-emerald-500/15 text-emerald-300"];
};

function PromotionForm({ open, onOpenChange, promo, onSaved }) {
  const { data: products } = useSWR(open ? "/admin/products" : null, fetcher);
  const [f, setF] = useState({});
  const [items, setItems] = useState({});
  const [pct, setPct] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const start = new Date();
    const end = new Date(Date.now() + 7 * 864e5);
    setF(promo ? { name: promo.name, label: promo.label, start_at: toLocal(promo.start_at), end_at: toLocal(promo.end_at), active: promo.active }
      : { name: "", label: "Oferta", start_at: toLocal(start.toISOString()), end_at: toLocal(end.toISOString()), active: true });
    setItems(Object.fromEntries((promo?.items || []).map((i) => [i.product_id, String(i.promo_price)])));
    setPct("");
  }, [open, promo]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const toggle = (p, on) => setItems((s) => {
    const n = { ...s };
    if (on) n[p.id] = pct ? (p.price * (1 - pct / 100)).toFixed(2) : "";
    else delete n[p.id];
    return n;
  });
  const applyPct = () => {
    const v = parseFloat(pct);
    if (!(v > 0 && v < 100)) return toast.error("Informe um percentual entre 1 e 99.");
    setItems((s) => Object.fromEntries(Object.keys(s).map((id) => [id, (products.find((p) => p.id === id).price * (1 - v / 100)).toFixed(2)])));
  };

  const submit = async (e) => {
    e.preventDefault();
    const list = Object.entries(items).map(([product_id, v]) => ({ product_id, promo_price: parseFloat(String(v).replace(",", ".")) }));
    if (!f.name.trim()) return toast.error("Informe o nome da promoção.");
    if (!list.length) return toast.error("Selecione ao menos um produto.");
    if (list.some((i) => !(i.promo_price > 0))) return toast.error("Informe o preço promocional de todos os produtos selecionados.");
    setSaving(true);
    try {
      const body = { ...f, start_at: new Date(f.start_at).toISOString(), end_at: new Date(f.end_at).toISOString(), items: list };
      if (promo) await api.put(`/admin/promotions/${promo.id}`, body);
      else await api.post("/admin/promotions", body);
      toast.success("Promoção salva");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border-[#D4AF37]/20 bg-[#141417]" data-testid="promotion-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-medium">{promo ? "Editar promoção" : "Nova promoção"}</DialogTitle>
          <DialogDescription>O preço promocional aparece no site somente durante o período definido.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Nome da promoção *"><Input value={f.name || ""} onChange={set("name")} data-testid="promotion-form-name" /></Field>
            <Field label="Etiqueta de oferta" hint="Ex.: Black Friday, Oferta, Dia das Mães"><Input value={f.label || ""} onChange={set("label")} maxLength={30} data-testid="promotion-form-label" /></Field>
            <Field label="Início *"><Input type="datetime-local" value={f.start_at || ""} onChange={set("start_at")} data-testid="promotion-form-start" /></Field>
            <Field label="Encerramento *"><Input type="datetime-local" value={f.end_at || ""} onChange={set("end_at")} data-testid="promotion-form-end" /></Field>
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.active} onCheckedChange={set("active")} data-testid="promotion-form-active" />Promoção ativa</label>
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-white/5 p-4">
            <Field label="Desconto em % para os selecionados"><Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="w-32" data-testid="promotion-form-percent" /></Field>
            <GhostButton type="button" onClick={applyPct} data-testid="promotion-form-apply-percent">Aplicar</GhostButton>
          </div>
          <div className="max-h-80 space-y-2 overflow-y-auto pr-1" data-testid="promotion-form-products">
            {(products || []).map((p) => {
              const on = p.id in items;
              return (
                <div key={p.id} className={`flex flex-wrap items-center gap-3 rounded-xl border p-3 ${on ? "border-[#D4AF37]/40" : "border-white/5"}`}>
                  <Checkbox checked={on} onCheckedChange={(v) => toggle(p, !!v)} data-testid={`promotion-product-check-${p.id}`} />
                  <div className="min-w-[150px] flex-1 text-sm">{p.name}<span className="block text-xs text-[#A1A1AA]">{p.brand} · original {brl(p.price)}</span></div>
                  {on && <Input inputMode="decimal" value={items[p.id]} onChange={(e) => setItems((s) => ({ ...s, [p.id]: e.target.value }))} placeholder="Preço promocional" className="w-36" data-testid={`promotion-product-price-${p.id}`} />}
                </div>
              );
            })}
            {products?.length === 0 && <p className="text-sm text-[#A1A1AA]">Cadastre produtos antes de criar promoções.</p>}
          </div>
          <div className="flex justify-end gap-3">
            <GhostButton type="button" onClick={() => onOpenChange(false)}>Cancelar</GhostButton>
            <GoldButton type="submit" disabled={saving} data-testid="promotion-form-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Salvar</GoldButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Promotions() {
  const { data, mutate } = useSWR("/admin/promotions", fetcher);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const remove = async () => {
    try {
      await api.delete(`/admin/promotions/${toDelete.id}`);
      toast.success("Promoção excluída");
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  return (
    <div data-testid="admin-promotions">
      <PageHeader title="Promoções" subtitle="Defina preços promocionais com período de validade">
        <GoldButton onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-add-promotion-button"><Plus className="h-4 w-4" />Nova promoção</GoldButton>
      </PageHeader>
      {data?.length === 0 && <p className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-sm text-[#A1A1AA]">Nenhuma promoção criada.</p>}
      <div className="space-y-3">
        {(data || []).map((p) => {
          const [label, cls] = status(p);
          return (
            <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/5 bg-[#141417] p-5" data-testid={`admin-promotion-row-${p.id}`}>
              <div className="min-w-[180px] flex-1">
                <p className="font-serif text-xl">{p.name} <span className="ml-2 rounded-full bg-[#D4AF37] px-2 py-0.5 align-middle font-sans text-[10px] font-bold uppercase text-black">{p.label}</span></p>
                <p className="text-xs text-[#A1A1AA]">{formatDate(p.start_at, true)} até {formatDate(p.end_at, true)} · {p.items.length} produto(s)</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs ${cls}`} data-testid={`promotion-status-${p.id}`}>{label}</span>
              <button onClick={() => { setEditing(p); setOpen(true); }} className="rounded-full p-2 text-[#A1A1AA] hover:text-[#F3E5AB]" aria-label="Editar" data-testid={`promotion-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => setToDelete(p)} className="rounded-full p-2 text-[#A1A1AA] hover:text-red-400" aria-label="Excluir" data-testid={`promotion-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
      <PromotionForm open={open} onOpenChange={setOpen} promo={editing} onSaved={mutate} />
      <ConfirmDelete open={!!toDelete} onCancel={() => setToDelete(null)} onConfirm={remove} title="Excluir promoção?" description={`“${toDelete?.name}” será removida.`} />
    </div>
  );
}
